<script>
  import { Flex, FlexItem } from "@gl/design-system";
  import { _, user } from "../stores/index";
  import InfoBox from "./InfoBox.svelte";

  export let allObjs;
  export let allActs;
  export let implicitPermissions;
  export let disabled;
  export let groupsPolicies;
  export let rolesPolicies;
  export let subPolicies;

  let form = {};

  async function mapSubPolicies(subPolicies) {
    form = {};

    if (subPolicies.length) {
      for (const policy of subPolicies) {
        form[policy] = true;
      }
    }
  }
  $: if (subPolicies) mapSubPolicies(subPolicies);

  export const getSelected = () => {
    let selected = [];
    for (const [key, value] of Object.entries(form)) {
      if (!key.includes("all") && value) {
        selected.push(key);
      }
    }

    return selected;
  };

  const checkAllActsOfObj = (e, objActs) => {
    Object.values(objActs).forEach(
      (policyID) => (form[policyID] = e.target.checked),
    );
  };
</script>

<div class="table my-5">
  <table>
    <thead>
      <tr>
        <th>
          <Flex class="align-items-center">{$_("common.app.type")}</Flex>
        </th>
        <th>
          <Flex class="align-items-center">{$_("common.policy")}</Flex>
        </th>
        {#each allActs as act}
          <th>
            <Flex class="align-items-center">
              {act}
              <InfoBox title={act} tooltipTop="100%"
                >{$_(`policies.act.${act}.description`)}</InfoBox
              >
            </Flex>
          </th>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#each allObjs as obj}
        <tr>
          <!-- all checkbox -->
          <td>
            <Flex>
              <div class="gl-checkbox mr-5">
                <label for={`all-${obj.obj}-${obj.appId}`}>
                  <input
                    id={`all-${obj.obj}-${obj.appId}`}
                    type="checkbox"
                    on:change={(e) => checkAllActsOfObj(e, obj.acts)}
                    indeterminate={Object.values(obj.acts).some(
                      (p) => form[p],
                    ) && !Object.values(obj.acts).every((p) => form[p])}
                    checked={Object.values(obj.acts).every((p) => form[p])}
                    disabled={disabled ||
                      !Object.values(obj.acts).every((a) =>
                        $user.policies.some((p) => p.id.toString() === a),
                      )}
                  />

                  <span
                    class="gl-toggle-shape"
                    id="span-all-{obj.obj}-{obj.appId}"
                  />
                </label>
              </div>

              <span title={obj.appName} class="app-name">
                {obj.appName}
              </span>
              <span class="color-grey">({obj.appType})</span>
            </Flex>
          </td>

          <!-- policy -->
          <td>
            <Flex>
              {obj.obj}
              <FlexItem pushEnd>
                <InfoBox title={`${$_("common.policy")}: ${obj.obj}`}>
                  {$_(`policies.obj.${obj.obj}.description`)}
                </InfoBox>
              </FlexItem>
            </Flex>
          </td>

          <!-- policy checkbox -->
          {#each allActs as act}
            {@const implicit = implicitPermissions.some(
              (ip) =>
                ip.implicit.includes(`${obj.obj}_${act}`) &&
                form[ip.parent] &&
                obj.appId === ip.appId,
            )}
            <td>
              {#if obj.acts[act]}
                <Flex class="align-items-center">
                  <div class="gl-checkbox">
                    <label for={obj.acts[act]} class="not-all">
                      <input
                        id={obj.acts[act]}
                        type="checkbox"
                        bind:checked={form[obj.acts[act]]}
                        indeterminate={implicit && !form[obj.acts[act]]}
                        disabled={disabled ||
                          !$user.policies.some(
                            (p) => p.id.toString() === obj.acts[act],
                          )}
                      />
                      <span
                        class:implicit-box={implicit}
                        class="gl-toggle-shape"
                      />
                    </label>
                  </div>

                  <!-- group/role legend -->
                  {#if groupsPolicies && groupsPolicies.includes(obj.acts[act])}
                    <div class="label-circle-group ml-1" />
                  {/if}
                  {#if rolesPolicies && rolesPolicies.includes(obj.acts[act])}
                    <div class="label-circle-role ml-1" />
                  {/if}
                </Flex>
              {/if}
            </td>
          {/each}
        </tr>
      {/each}
    </tbody>
  </table>
</div>

<style>
  .table {
    flex: 1;
    min-height: 0;
    overflow: auto;
    width: 100%;
  }
  table {
    border-collapse: separate;
    border-spacing: 0 0.5em;
    position: relative;
    word-break: keep-all;
  }

  th {
    background-color: var(--gl-background);
    color: var(--gl-text);
    font-weight: bold;
    padding: 10px;
    border-right: 1px solid var(--gl-border);
    position: sticky;
    top: 0;
    z-index: 1;
    font-size: 14px;
  }

  th:last-child {
    border-right: none;
  }

  tr {
    background-color: var(--gl-scheme-main);
  }

  td {
    border-right: 1px solid var(--gl-border);
    border-top: 1px solid var(--gl-border);
    border-bottom: 1px solid var(--gl-border);
    padding: 10px;
    position: relative;
  }

  td:first-child {
    border-left: 1px solid var(--gl-border);
  }

  .app-name {
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    word-break: break-all;
    white-space: nowrap;
  }

  .gl-checkbox {
    display: flex;
    justify-content: center;
  }

  .label-circle-group,
  .label-circle-role {
    width: 8px;
    height: 8px;
    border-radius: 50%;
  }

  .label-circle-group {
    background-color: var(--gl-primary);
  }

  .label-circle-role {
    background-color: var(--gl-warning);
  }

  /* custom checkbox for implicit permissions */
  input:indeterminate ~ .implicit-box {
    background-color: var(--gl-background);
    border: none;
  }
  input:indeterminate ~ .implicit-box::before {
    display: block;
    border-width: 0.125em;
    height: 0.6875em;
    left: calc(50% - 0.175em);
    position: absolute;
    top: calc(50% - 0.4375em);
    width: 0.4375em;
  }
  input:indeterminate ~ .implicit-box::after {
    display: none;
  }
</style>
